import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';

export default function Calculator() {
  const [contractAddress, setContractAddress] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [ethPrice, setEthPrice] = useState(null);
  
  const [tokenValue, setTokenValue] = useState(10000);
  const [duration, setDuration] = useState(7);
  const [sellTax, setSellTax] = useState(1.0);
  
  const [quote, setQuote] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const marketCap = tokenData?.market_cap || 0;
  const autoTier = marketCap >= 2000000 ? 'tier3' : marketCap >= 500000 ? 'tier2' : 'tier1';
  const ltvRates = { tier1: 0.40, tier2: 0.50, tier3: 0.55 };
  const ltv = ltvRates[autoTier];

  const sellTaxAmount = tokenValue * (sellTax / 100);
  const priceImpact = tokenValue * 0.10;
  const netValue = tokenValue - sellTaxAmount - priceImpact;
  const loanAmount = netValue * ltv;
  const interest = loanAmount * 0.01 * duration;
  const platformFee = loanAmount * 0.02;
  const youReceive = loanAmount - interest - platformFee;

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    async function fetchEthPrice() {
      try {
        const res = await fetch(`${API_BASE}/api/enaibl/eth-price`);
        const data = await res.json();
        setEthPrice(data.eth_price || 3200);
      } catch { setEthPrice(3200); }
    }
    fetchEthPrice();
  }, [API_BASE]);

  const wethLiquidityUSD = tokenData?.liquidity_weth && ethPrice 
    ? tokenData.liquidity_weth * ethPrice : null;

  async function handleVerify() {
    if (!contractAddress || !contractAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      setErrorMsg('Please enter a valid contract address (0x...)');
      return;
    }
    setVerifying(true);
    setErrorMsg('');
    setTokenData(null);
    setQuote(null);
    setVerified(null);

    try {
      const verifyRes = await fetch(`${API_BASE}/api/enaibl/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: contractAddress })
      });
      const verifyData = await verifyRes.json();
      if (!verifyData.verified) {
        setVerified(false);
        setErrorMsg(verifyData.reason || 'Token not found on Robinhood Network');
        setVerifying(false);
        return;
      }
      const tokenRes = await fetch(`${API_BASE}/api/enaibl/token/${contractAddress}`);
      const tokenInfo = await tokenRes.json();
      if (tokenInfo.error || tokenInfo.detail) {
        setErrorMsg(tokenInfo.error || tokenInfo.detail || 'No trading pairs found');
        setVerified(false);
        setVerifying(false);
        return;
      }
      setVerified(true);
      setTokenData(tokenInfo);
      await calculateQuote(tokenInfo);
    } catch (err) {
      setErrorMsg('Verification failed. Please try again.');
      setVerified(false);
    }
    setVerifying(false);
  }

  async function calculateQuote(data) {
    const tokenInfo = data || tokenData;
    if (!tokenInfo || !contractAddress) return;
    setCalculating(true);
    try {
      const res = await fetch(`${API_BASE}/api/enaibl/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: contractAddress,
          token_value_usd: tokenValue,
          duration_days: duration,
          sell_tax_pct: sellTax
        })
      });
      const quoteData = await res.json();
      if (quoteData.error || quoteData.detail) {
        setErrorMsg(typeof quoteData.detail === 'string' ? quoteData.detail : 'Failed to calculate quote');
        setQuote(null);
      } else {
        setQuote(quoteData);
        setErrorMsg('');
      }
    } catch { setErrorMsg('Failed to calculate quote'); }
    setCalculating(false);
  }

  useEffect(() => {
    if (verified && tokenData) {
      const timer = setTimeout(() => calculateQuote(), 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenValue, duration, sellTax, verified, tokenData]);

  const formatUSD = (n) => n?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
  const formatCompact = (n) => {
    if (!n) return '$0';
    if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(2)}`;
  };

  return (
    <div className="relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-8 sm:mb-10"
      >
        <span 
          className="inline-block text-[11px] uppercase tracking-[0.08em] text-white/50 px-4 py-1.5 rounded-full mb-4"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          Simulate
        </span>
        <h3 className="text-[22px] sm:text-[28px] font-semibold text-white">See your agent's loan in real time.</h3>
        <p className="text-[13px] sm:text-[14px] text-white/40 mt-2 px-4">Enter a token contract address on Robinhood Network to simulate a borrow.</p>
      </motion.div>

      {/* Calculator Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-[780px] mx-auto rounded-[20px] overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(16px)',
        }}
        data-testid="loan-calculator"
      >
        {/* Token Input */}
        <div className="p-4 sm:p-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <label className="text-[11px] text-white/50 uppercase tracking-wider mb-3 block">Verify Token</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Paste token contract address (Robinhood)"
              value={contractAddress}
              onChange={(e) => setContractAddress(e.target.value)}
              className="flex-1 bg-black/50 text-white text-[13px] sm:text-[14px] outline-none px-4 py-3 rounded-xl placeholder:text-white/25"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}
              data-testid="contract-address-input"
            />
            <motion.button
              onClick={handleVerify}
              disabled={verifying}
              className="px-6 py-3 rounded-xl font-medium text-[14px] text-white whitespace-nowrap w-full sm:w-auto"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)' }}
              whileHover={{ background: 'rgba(255,255,255,0.15)' }}
              data-testid="verify-token-btn"
            >
              {verifying ? <><Loader2 className="animate-spin inline mr-2" size={16} />Verifying...</> : 'Verify Token'}
            </motion.button>
          </div>
          
          <AnimatePresence>
            {verified === true && tokenData && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 p-4 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="token-verified-status"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white/80">✓</span>
                  <span className="text-white font-semibold">{tokenData.name}</span>
                  <span className="text-white/50">(${tokenData.symbol})</span>
                </div>
                <div className="flex flex-wrap gap-3 sm:gap-4 text-[11px] sm:text-[12px]">
                  <span className="text-white/40">Price: <span className="text-white">${tokenData.price_usd?.toFixed(6)}</span></span>
                  <span className="text-white/40">MC: <span className="text-white">{formatCompact(tokenData.market_cap)}</span></span>
                  <span className="text-white/40">Liq: <span className="text-white">{formatCompact(tokenData.liquidity_usd)}</span></span>
                  {wethLiquidityUSD && <span className="text-white/40">WETH Liq: <span className="text-white">{formatCompact(wethLiquidityUSD)}</span></span>}
                </div>
              </motion.div>
            )}
            {verified === false && errorMsg && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
                <span className="text-white/50 text-[13px]">✗ {errorMsg}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Calculator Body */}
        <div className="p-5 sm:p-8 grid md:grid-cols-2 gap-6 sm:gap-8" style={{ opacity: verified ? 1 : 0.4, pointerEvents: verified ? 'auto' : 'none' }}>
          {/* Inputs */}
          <div className="space-y-5 sm:space-y-6">
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider">Token Value</label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-white/40">$</span>
                <input type="number" value={tokenValue} onChange={(e) => setTokenValue(Math.max(100, Math.min(100000, Number(e.target.value))))} className="bg-transparent text-[20px] sm:text-[24px] font-bold text-white w-full outline-none" data-testid="token-value-input" />
              </div>
              <input type="range" min="100" max="100000" value={tokenValue} onChange={(e) => setTokenValue(Number(e.target.value))} className="calc-slider w-full mt-3" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider">Market Cap Tier {tokenData && <span className="text-white/25">(auto)</span>}</label>
              <div className="mt-3 space-y-2">
                {[{ id: 'tier1', label: '< $500K', rate: '40%' }, { id: 'tier2', label: '$500K – $2M', rate: '50%' }, { id: 'tier3', label: '$2M+', rate: '55%' }].map((tier) => (
                  <div key={tier.id} className="flex items-center gap-3">
                    <div className={`w-3.5 h-3.5 rounded-full border-2 ${autoTier === tier.id ? 'bg-white border-white' : 'border-white/20'}`} />
                    <span className={`text-[12px] sm:text-[13px] ${autoTier === tier.id ? 'text-white' : 'text-white/40'}`}>{tier.label}</span>
                    <span className="text-[11px] sm:text-[12px] text-white/30 ml-auto">{tier.rate}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider">Duration</label>
              <div className="flex items-center gap-2 mt-2">
                <input type="number" value={duration} onChange={(e) => setDuration(Math.max(3, Math.min(90, Number(e.target.value))))} className="bg-transparent text-[16px] sm:text-[18px] font-bold text-white w-14 outline-none" data-testid="duration-input" />
                <span className="text-white/40 text-[13px]">days</span>
              </div>
              <input type="range" min="3" max="90" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="calc-slider w-full mt-2" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider">Sell Tax</label>
              <div className="flex items-center gap-2 mt-2">
                <input type="number" step="0.1" value={sellTax} onChange={(e) => setSellTax(Math.max(0, Math.min(2, Number(e.target.value))))} className="bg-transparent text-[16px] sm:text-[18px] font-bold text-white w-14 outline-none" data-testid="sell-tax-input" />
                <span className="text-white/40 text-[13px]">%</span>
              </div>
              <input type="range" min="0" max="2" step="0.1" value={sellTax} onChange={(e) => setSellTax(Number(e.target.value))} className="calc-slider w-full mt-2" />
            </div>
          </div>

          {/* Results */}
          <div className="space-y-3 text-[12px] sm:text-[13px]">
            {calculating && <div className="flex items-center gap-2 text-white/50 mb-4"><Loader2 className="animate-spin" size={14} />Calculating...</div>}
            <div className="flex justify-between"><span className="text-white/50">Token value</span><span className="text-white">${quote ? formatUSD(quote.token_value_usd) : formatUSD(tokenValue)}</span></div>
            <div className="flex justify-between"><span className="text-white/35">Sell tax ({quote?.sell_tax_pct || sellTax}%)</span><span className="text-white/50">- ${quote ? formatUSD(quote.sell_tax_usd) : formatUSD(sellTaxAmount)}</span></div>
            <div className="flex justify-between"><span className="text-white/35">Price impact (~{quote?.price_impact_pct || 10}%)</span><span className="text-white/50">- ${quote ? formatUSD(quote.price_impact_usd) : formatUSD(priceImpact)}</span></div>
            <div className="flex justify-between font-semibold pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}><span className="text-white">Net value</span><span className="text-white">${quote ? formatUSD(quote.net_value_usd) : formatUSD(netValue)}</span></div>
            <div className="flex justify-between pt-2"><span className="text-white/50">LTV rate</span><span className="text-white">{quote ? `${(quote.ltv_rate * 100).toFixed(0)}%` : `${(ltv * 100).toFixed(0)}%`}</span></div>
            <div className="flex justify-between font-semibold pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}><span className="text-white">Loan amount</span><span className="text-white">${quote ? formatUSD(quote.loan_amount_usd) : formatUSD(loanAmount)}</span></div>
            <div className="flex justify-between"><span className="text-white/35">Interest ({quote?.duration_days || duration}d)</span><span className="text-white/50">- ${quote ? formatUSD(quote.interest_prepaid_usd) : formatUSD(interest)}</span></div>
            <div className="flex justify-between"><span className="text-white/35">Platform fee (2%)</span><span className="text-white/50">- ${quote ? formatUSD(quote.platform_fee_usd) : formatUSD(platformFee)}</span></div>
            <div className="pt-4 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Agent Receives</div>
              <motion.div className="text-[22px] sm:text-[26px] font-bold text-white" style={{ textShadow: '0 0 30px rgba(255,255,255,0.15)' }} data-testid="agent-receives-amount">
                ${quote ? formatUSD(quote.you_receive_usd) : formatUSD(youReceive)}
                {quote?.you_receive_eth && <span className="text-[13px] sm:text-[15px] text-white/50 ml-2">≈ {quote.you_receive_eth} ETH</span>}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
